import React, { useState } from 'react';
import { ContainedButton, defaultImage } from '@guryou/html-components';
import { Box, Checkbox, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useAiContext } from './context/AiContext';
const drawerWidth = 240;

const useStyles = makeStyles(theme => ({
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 50px)',
    marginTop: '50px',
    padding: theme.spacing(3),
    backgroundColor: '#212121',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  headerRow: {
    color: '#ccc',
    display: 'flex',
    padding: '12px',
    borderBottom: '1px solid #444',
    backgroundColor: '#2a2a2a',
    fontWeight: 'bold',
  },
  row: {
    color: '#ccc',
    '& *': {
      color: '#ccc !important',
    },
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
    borderBottom: '1px solid #444',
    '&:hover': {
      backgroundColor: '#2e2e2e',
    },
  },
  col: {
    padding: '0 8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  flexCol2: {
    flex: 2,
  },
  flexCol: {
    flex: 1,
  },
  checkboxCol: {
    flex: '0 0 48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageCol: {
    flex: '0 0 64px',
    display: 'flex',
    alignItems: 'center',
  },
  image: {
    width: 48,
    height: 48,
    borderRadius: 4,
    objectFit: 'cover',
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
}));

const truncateText = (text, maxChars) => (text.length > maxChars ? text.slice(0, maxChars) + '...' : text);

const ExercisePreview = ({ open }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const classes = useStyles();

  const { exercises } = useAiContext();

  const isSelected = id => selectedIds.includes(id);

  const toggleSelection = id => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
  };

  const selectAll = () => setSelectedIds(exercises.map(e => e.id));
  const deselectAll = () => setSelectedIds([]);

  return (
    <main
      className={clsx(classes.content, {
        [classes.contentShift]: open,
      })}
    >
      <Box className={classes.controls}>
        <div>
          <ContainedButton onClick={selectAll}>Select All</ContainedButton>
          <ContainedButton onClick={deselectAll} color="secondary" style={{ marginLeft: 8 }}>
            Deselect All
          </ContainedButton>
        </div>
        <ContainedButton color="secondary" disabled={!selectedIds.length}>
          Import {selectedIds.length} Selected
        </ContainedButton>
      </Box>

      <Box className={classes.headerRow}>
        <div className={clsx(classes.col, classes.checkboxCol)} />
        <div className={clsx(classes.col, classes.imageCol)}>Image</div>
        <div className={clsx(classes.col, classes.flexCol2)}>Title</div>
        <div className={clsx(classes.col, classes.flexCol)}>Level</div>
        <div className={clsx(classes.col, classes.flexCol)}>Muscles</div>
      </Box>

      <div style={{ overflow: 'auto' }}>
        {exercises.map(ex => (
          <Box key={ex.id} className={classes.row}>
            <div className={clsx(classes.col, classes.checkboxCol)}>
              <Checkbox checked={isSelected(ex.id)} onChange={() => toggleSelection(ex.id)} />
            </div>
            <div className={clsx(classes.col, classes.imageCol)}>
              <img
                src={ex?.image?.thumbnailUrl || defaultImage}
                alt={ex?.name || 'default'}
                className={classes.image}
                loading="lazy"
                onError={e => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = defaultImage;
                }}
              />
            </div>
            <div className={clsx(classes.col, classes.flexCol2)}>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {ex?.name || 'No title found'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {truncateText(ex.description || 'No description', 120)}
              </Typography>
            </div>
            <div className={clsx(classes.col, classes.flexCol)}>{ex.level}</div>
            <div className={clsx(classes.col, classes.flexCol)}>{ex.muscleArea}</div>
          </Box>
        ))}
      </div>
    </main>
  );
};

export default ExercisePreview;
